import { UseMutationOptions } from '@tanstack/react-query';
import { errorLogger } from '@shared/services/ErrorLogger';

// --- Shared Types ---

export interface ApiError extends Error {
  status?: number;
  statusText?: string;
  endpoint?: string;
  method?: string;
}

// For Mutation Options passed from components
export interface MutateOptions<TData = unknown, TVariables = void> {
  onSuccess?: (data: TData, variables: TVariables, context: unknown) => void;
  onError?: (error: ApiError, variables: TVariables, context: unknown) => void;
  onSettled?: (
    data: TData | undefined,
    error: ApiError | null,
    variables: TVariables,
    context: unknown
  ) => void;
}

// --- Shared API Helpers ---

export const getCsrfToken = (): string => {
  // Always query the DOM for the latest CSRF token to handle token rotation
  const metaTag = document.querySelector(
    '[name=csrf-token]'
  ) as HTMLMetaElement;
  const token = metaTag?.content || '';

  return token;
};

export const getDefaultHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Key-Inflection': 'camel',
  };

  // Always try to include CSRF token from meta tag
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
};

export const getDefaultFetchOptions = (): RequestInit => {
  return {
    headers: getDefaultHeaders(),
    credentials: 'include',
  };
};

export const handleApiResponse = async (
  response: Response,
  requestInfo?: { method?: string; endpoint?: string; requestBody?: any }
) => {
  if (response.status === 204) {
    return null;
  }
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      // If we can't parse the error response, create a basic error
      const error: ApiError = new Error(
        response.statusText || `Request failed with status ${response.status}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = requestInfo?.endpoint || response.url;
      error.method = requestInfo?.method;

      // Log API error
      errorLogger.logApiErrorWithContext(
        error,
        requestInfo?.method || 'GET',
        requestInfo?.endpoint || response.url,
        response.status,
        requestInfo?.requestBody,
        undefined
      );

      throw error;
    }

    let message = 'An unexpected error occurred.';

    // Extract error message from various response formats
    if (errorData?.error) {
      // Direct error message from server (our preferred format)
      message = errorData.error;
    } else if (
      errorData?.status?.errors &&
      Array.isArray(errorData.status.errors)
    ) {
      // Array of error messages
      message = errorData.status.errors.join(', ');
    } else if (errorData?.status?.message) {
      // Message in status object
      message = errorData.status.message;
    } else if (errorData?.errors && Array.isArray(errorData.errors)) {
      // Array of errors at root level
      message = errorData.errors.join(', ');
    } else if (response.statusText) {
      // Fallback to HTTP status text
      message = response.statusText;
    } else {
      // Final fallback
      message = `Request failed with status ${response.status}`;
    }

    const error: ApiError = new Error(message);
    error.status = response.status;
    error.statusText = response.statusText;
    error.endpoint = requestInfo?.endpoint || response.url;
    error.method = requestInfo?.method;

    // Log API error with context
    errorLogger.logApiErrorWithContext(
      error,
      requestInfo?.method || 'GET',
      requestInfo?.endpoint || response.url,
      response.status,
      requestInfo?.requestBody,
      errorData
    );

    throw error;
  }
  return response.json();
};

// Utility to refresh CSRF token by making a GET request to any Rails endpoint
export const refreshCsrfToken = async (): Promise<void> => {
  try {
    // Make a simple GET request to refresh the session and get a new CSRF token
    const response = await fetch('/api/me', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    // The response doesn't matter, we just need the new CSRF token in the meta tag
    if (response.ok) {
      // Wait a bit for the DOM to update with new token
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('CSRF token refreshed');
    }
  } catch (error) {
    errorLogger.logError(new Error('Failed to refresh CSRF token: ' + error));
  }
};

// Enhanced fetch with automatic CSRF retry
export const fetchWithCsrfRetry = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const requestOptions = {
    ...getDefaultFetchOptions(),
    ...options,
    headers: {
      ...getDefaultHeaders(),
      ...(options.headers || {}),
    },
  };

  let response = await fetch(url, requestOptions);

  // If we get a 401 CSRF error, try refreshing the token and retry once
  if (response.status === 401) {
    try {
      const errorData = await response.clone().json();
      if (errorData?.error?.includes('CSRF')) {
        await refreshCsrfToken();

        // Update headers with new CSRF token
        const newHeaders = {
          ...requestOptions.headers,
          ...getDefaultHeaders(),
        };

        // Retry the request with new token
        response = await fetch(url, {
          ...requestOptions,
          headers: newHeaders,
        });
      }
    } catch {
      // If we can't parse the error, just return the original response
      errorLogger.logError(
        new Error('Could not parse error response for CSRF retry')
      );
    }
  }

  return response;
};

// --- Shared Mutation Helper ---

export const createMutationOptions = <TData = unknown, TVariables = void>(
  options?: MutateOptions<TData, TVariables>
): UseMutationOptions<TData, ApiError, TVariables> => ({
  onError: (error, variables, context) => {
    errorLogger.logError(new Error(`Mutation Error: ${error.message}`));

    // If it's a CSRF error, suggest page refresh
    if (error.status === 401 && error.message.includes('CSRF')) {
      errorLogger.logError(
        new Error('CSRF token issue detected. Consider refreshing the page.')
      );
    }

    options?.onError?.(error, variables, context);
  },
  onSuccess: (data, variables, context) => {
    options?.onSuccess?.(data, variables, context);
  },
  onSettled: (data, error, variables, context) => {
    options?.onSettled?.(data, error, variables, context);
  },
});
